import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import {
  DeliveredDamagesFollowUpSchema,
  INIT_DELIVERED_DAMAGES_FOLLOW_UP_FORM_VALUE,
} from "../../schema/prInvestigation";
import Loader from "../../components/loader/loader";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import {
  DD_FOLLOW_UP_FEILDS,
  EFormAction,
  EOsdType,
  EPRStatus,
  ErrorMessages,
  FORM_STYLES,
  XL_GRID_PROPS,
} from "../../constants";
import { isFormValueChanged } from "../../AppState/isFormValueChanged";
import { compareObjects } from "../../helpers";
import { IDDFollowUp } from "../../models/prException";
import { DamageFollowupAPI } from "../../API/DamageFollowupAPI";
import TooltipWithClickAway from "../../components/terminalPicker/TooltipWithClickAway";
import { useImageUploader } from "../../@hooks/useImageUploader";
import NoteBox from "../../components/noteBox/NoteBox";
import PRInvestigationTemplate from "../../templates/prInvestigationTemplate";
import AppState from "../../AppState";

type Props = {
  osdNumber?: number;
  osdDetails?: any;
  setInvestigationStatus: (status: number) => void;
  isCleared?: boolean
};


const DeliveredDamagesFollowUp: React.FC<Props> = ({
  osdNumber = 0,
  osdDetails,
  setInvestigationStatus,
  isCleared
}) => {
  const dispatch = useDispatch();
  const { uploadImage } = useImageUploader();
  const investigationDetails = useMemo(() => osdDetails, [osdDetails]);
  const [loading, setLoading] = useState(false);
  const activeTerminal = useSelector((state: AppState) => state.activeTerminal);
  const [resetTimestamps, setResetTimestamps] = useState<string>(
    Date.now().toString()
  );
  const [status, setStatus] = useState(0);
  const [formFieldsDisabled, setFormFieldsDisabled] = useState(true);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [initValues, setInitValues] = React.useState<IDDFollowUp>(
    INIT_DELIVERED_DAMAGES_FOLLOW_UP_FORM_VALUE
  );

  const {
    errors,
    values,
    handleBlur,
    handleChange,
    resetForm,
    setFieldValue,
    validateForm,
    handleSubmit,
  } = useFormik({
    validationSchema: DeliveredDamagesFollowUpSchema,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: initValues,
    onSubmit: (values) => {
      setIsCompleting(true);
    },
  });

  const enableEditing = () => {
    setFormFieldsDisabled(false);
  };

  const updateInitValue = (
    key: string,
    value: string | number | null | undefined
  ) => {
    setInitValues((prevState) => {
      // Update the top-level property
      return {
        ...prevState,
        [key]: value,
      };
    });
  };

  // Follow-up action
  const saveOrComplete = useCallback(
    async (action = EFormAction.save) => {
      const params: IDDFollowUp = { ...values }; // shallow clone is usually sufficient
      const errors = await validateForm();

      const hasValidationError = Object.keys(errors).length > 0;

      const validYN = new Set(["Y", "N"]);

      (
        [
          "wasCorrectDeliveryStatusEntered",
          "confirmDeliveryCodeMatchesADE",
          "confirmDriverTookValidEPs",
        ] as (keyof IDDFollowUp)[]
      ).forEach((key) => {
        if (!validYN.has(params[key] as string)) {
          delete params[key];
        }
      });

      if (action !== EFormAction.save && hasValidationError) {
        setIsCompleting(true);
        return;
      }

      setLoading(true);

      try {
        const { statusNumber } = await DamageFollowupAPI(
          params,
          osdNumber,
          action,
          EOsdType.dd,
          activeTerminal?.terminalId
        );
        setStatus(statusNumber);
        setIsCompleting(false);
        setInvestigationStatus(statusNumber);
        setFormFieldsDisabled(true);
        patchFormFieldValues(params);

        const message =
          action === EFormAction.complete
            ? ErrorMessages.investigationNoteCompleted
            : ErrorMessages.followUpSave;

        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.SUCCESS,
            message,
            Date.now().toString()
          )
        );

        if (action === EFormAction.complete) {
          await uploadInvestigation();
        }
      } catch (err) {
        const errorMessage =
          typeof err === "string"
            ? err
            : "Exception follow-up failed. Please try again.";

        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.ERROR,
            errorMessage,
            Date.now().toString()
          )
        );
        console.error("Exception follow-up error:", err);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, dispatch, validateForm, osdNumber]
  );

  const uploadInvestigation = async () => {
    await uploadImage({
      elementId: "dd-image",
      osdNumber: investigationDetails?.osdNumber ?? 0,
      setLoading: setLoading,
       headers: { "OSD-Module-Name": "damagesOrRefusals", "OSD-Section-Name": "followup", "OSD-Action-Type": "upload", "OSD-Exception-Type": "DD",
        "OSD-Destination-Terminal": activeTerminal?.terminalId ?? "" },
    });
    setLoading(true);
  };

  const resetToLastSaved = () => {
    resetForm(); // Reset to last saved values
    setResetTimestamps(Date.now().toString());
    patchFormFieldValues(initValues);
  };

  const patchFormFieldValues = (formValues: IDDFollowUp) => {
    DD_FOLLOW_UP_FEILDS.forEach((key: keyof IDDFollowUp) => {
      const value = formValues[key] ?? undefined;
      setFieldValue(key, value, true);
      updateInitValue(key, value);
    });
  };

  const getErrorMessage = (field: keyof IDDFollowUp) => {
    if (errors?.[field] && isCompleting) {
      return <span className="form-error">{errors?.[field]}</span>;
    }
    return null;
  };

  useEffect(() => {
    dispatch(isFormValueChanged(!compareObjects(initValues, values)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, initValues]);

  useEffect(() => {
    if (!investigationDetails) return;
    patchFormFieldValues(investigationDetails);
    if (investigationDetails?.investigationStatusNumber)
      setStatus(investigationDetails?.investigationStatusNumber);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationDetails]);

  return (
    <>
      <form action="" key={resetTimestamps} onSubmit={handleSubmit}>
        <Grid
          container
          spacing={2}
          className="filter-margin accordion-container"
        >
          <Grid item {...XL_GRID_PROPS}>
            <FormControl className="radio-group question-group">
              <FormLabel>
                Was the correct delivery status entered? <sup>*</sup>
              </FormLabel>
              <RadioGroup
                data-testid="wasCorrectDeliveryStatusEntered"
                row
                name="wasCorrectDeliveryStatusEntered"
              >
                <FormControlLabel
                  value="Y"
                  control={<Radio disabled={formFieldsDisabled} />}
                  label="Yes"
                  name="wasCorrectDeliveryStatusEntered"
                  data-testid="wasCorrectDeliveryStatusEnteredY"
                  checked={values?.wasCorrectDeliveryStatusEntered === "Y"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <FormControlLabel
                  value="N"
                  control={<Radio disabled={formFieldsDisabled} />}
                  label="No"
                  name="wasCorrectDeliveryStatusEntered"
                  data-testid="wasCorrectDeliveryStatusEnteredN"
                  checked={values?.wasCorrectDeliveryStatusEntered === "N"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </RadioGroup>
              {getErrorMessage("wasCorrectDeliveryStatusEntered")}

              {values?.wasCorrectDeliveryStatusEntered === "N" && (
                <NoteBox
                  data-testid="delivery-status-note"
                  message="Note: Correct delivery status. Use option 86, from terminal operations menu, to update delivery status."
                />
              )}
            </FormControl>
          </Grid>

          <Grid item {...XL_GRID_PROPS} className="pt-0">
            <FormControl className="radio-group question-group">
              <FormLabel>
                Confirm the delivery code matches the ADE <sup>*</sup>
                &nbsp;
                <TooltipWithClickAway
                  tooltipText="This will be populated correctly in the F7."
                  tooltipKey="tooltip2"
                />
              </FormLabel>
              <RadioGroup
                data-testid="confirmDeliveryCodeMatchesADE"
                row
                name="confirmDeliveryCodeMatchesADE"
              >
                <FormControlLabel
                  value="Y"
                  control={<Radio disabled={formFieldsDisabled} />}
                  label="Yes"
                  name="confirmDeliveryCodeMatchesADE"
                  data-testid="confirmDeliveryCodeMatchesADEY"
                  checked={values?.confirmDeliveryCodeMatchesADE === "Y"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <FormControlLabel
                  value="N"
                  control={<Radio disabled={formFieldsDisabled} />}
                  label="No"
                  name="confirmDeliveryCodeMatchesADE"
                  data-testid="confirmDeliveryCodeMatchesADEN"
                  checked={values?.confirmDeliveryCodeMatchesADE === "N"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                {values?.confirmDeliveryCodeMatchesADE === "N" && (
                  <div className="tooltip-custom">
                    <TooltipWithClickAway
                      tooltipText="Use option 86, from terminal operations menu, to enter a delivery status."
                      tooltipKey="tooltip3"
                    />
                  </div>
                )}
              </RadioGroup>
              {getErrorMessage("confirmDeliveryCodeMatchesADE")}
              {values?.confirmDeliveryCodeMatchesADE === "N" && (
                <NoteBox message="Note: Enter delivery status." />
              )}
            </FormControl>
          </Grid>

          <Grid item {...XL_GRID_PROPS} className="pt-0">
            <FormControl className="radio-group question-group">
              <FormLabel>
                Confirm the driver took valid EP’s <sup>*</sup>
              </FormLabel>
              <RadioGroup
                row
                name="confirmDriverTookValidEPs"
                data-testid="confirmDriverTookValidEPs"
                value={values?.confirmDriverTookValidEPs}
              >
                <FormControlLabel
                  value="Y"
                  control={<Radio disabled={formFieldsDisabled} />}
                  label="Yes"
                  data-testid="confirmDriverTookValidEPsY"
                  checked={values?.confirmDriverTookValidEPs === "Y"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <FormControlLabel
                  value="N"
                  control={<Radio disabled={formFieldsDisabled} />}
                  label="No"
                  data-testid="confirmDriverTookValidEPsN"
                  checked={values?.confirmDriverTookValidEPs === "N"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </RadioGroup>
              {getErrorMessage("confirmDriverTookValidEPs")}
              {values?.confirmDriverTookValidEPs === "N" && (
                <NoteBox message="Note: Notify terminal manager." />
              )}
              {values?.confirmDriverTookValidEPs === "Y" && (
                <NoteBox message="Note: Clear the DD exception in the 78-6." />
              )}
            </FormControl>
          </Grid>

          <Grid item {...XL_GRID_PROPS} className="pt-0">
            <FormControl fullWidth size="small">
              <InputLabel style={FORM_STYLES.LABEL} shrink={true}>
                F18 notes <sup>*</sup>
              </InputLabel>
              <TextField
                autoComplete="off"
                multiline
                rows={2}
                name="f18Note"
                data-testid="f18Note"
                value={values?.f18Note}
                onChange={handleChange}
                placeholder="Enter F18 notes "
                fullWidth
                inputProps={{ maxLength: 300 }}
                disabled={formFieldsDisabled}
                variant="outlined"
              />
            </FormControl>
            {getErrorMessage("f18Note")}
            <br />
            <br />
            <NoteBox message="Note: If the damage freight is delivered and there are valid EP's image this checklist. DD is completed." />
          </Grid>

          {/* Action Buttons */}
          {status !== EPRStatus.needsCleared && !isCleared && (
            <Grid item {...XL_GRID_PROPS} className="btn-group-submit">
              {formFieldsDisabled ? (
                <Button
                  variant="contained"
                  data-testid="edit-button"
                  onClick={enableEditing}
                  className="assign ml mr-0"
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    onClick={() => saveOrComplete(EFormAction.save)}
                    className="entry ml"
                    type="submit"
                  >
                    Save
                  </Button>
                  <Button
                    variant="contained"
                    onClick={resetToLastSaved}
                    className="clear-btn mr-0 ml"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    data-testid="complete-button"
                    onClick={() => saveOrComplete(EFormAction.complete)}
                    className="entry ml"
                    type="submit"
                  >
                    Complete & Image
                  </Button>
                </>
              )}
            </Grid>
          )}
        </Grid>
      </form>
      <div className="hide-me" id={`dd-image`}>
        <PRInvestigationTemplate
          osdType={EOsdType.dd}
          values={values}
          osdNumber={osdNumber}
        />
      </div>
      <Loader pageLoader={loading} />
    </>
  );
};

export default React.memo(DeliveredDamagesFollowUp);
